import FacetList from "../components/FacetList";
import FacetPie from "../components/FacetPie";
import FacetBar from "../components/FacetBar";
import FacetKaplanMeier from "../components/FacetKaplanMeier";


export const CATEGORY_HEADERS = [
    {
        key: "header-patient",
        label: "Patient characteristics",
        col: 1,
        colSpan: 8,
        row: 1,
        statKey: "patients",
        countLabel: "patients",
        color: "#2F7F7A",
        bgColor: "#E6F3F1",
    },
    {
        key: "header-treatment",
        label: "Treatment characteristics",
        col: 1,
        colSpan: 6,
        row: 4,
        statKey: "treatments",
        countLabel: "treatments",
        color: "#2E5AAC",
        bgColor: "#E8EEFA",
    },
    {
        key: "header-manuscript",
        label: "Manuscript characteristics",
        col: 7,
        colSpan: 2,
        row: 4,
        statKey: "publications",
        countLabel: "publications",
        color: "#5A6772",
        bgColor: "#F1F3F5",
    },
];

export const TILES = [
    // --- patient-characteristics ---
    {
        key: "cancerOrgans",
        title: "Primary cancer site",
        iconHover: "Patient-level data. Each publication reports data on one or more patients, and each patient may have outcomes associated with one or more treatments.",
        description: "Indicates the primary cancer site involved in the patient\u2019s diagnosis. If a patient developed separate primary cancers involving different organs, each is displayed separately in this panel.",
        Component: FacetList,
        tileProps: { cols: 2, rows: 2, col: 1, row: 2, category: "patient-characteristics" },
        facetProps: { category: "patient-characteristics" },
    },
    {
        key: "cancerTypes",
        title: "Cancer type details",
        iconHover: "Patient-level data. Each publication reports data on one or more patients, and each patient may have outcomes associated with one or more treatments.",
        description: "Indicates the patient diagnosis as reported in the original manuscript and mapped to the OncoTree taxonomy. If a patient developed separate primary cancers, these are detailed together and separated by \u201cAND\u201d in this panel.",
        Component: FacetList,
        tileProps: { cols: 2, rows: 2, col: 3, row: 2, category: "patient-characteristics" },
        facetProps: { category: "patient-characteristics" },
    },
    {
        key: "metastaticPatterns",
        title: "Metastatic pattern details",
        iconHover: "Patient-level data. Each publication reports data on one or more patients, and each patient may have outcomes associated with one or more treatments.",
        description: "Indicates the site(s) of metastatic disease reported in the patient. If multiple metastatic sites are reported, these are detailed together and separated by \u201cAND\u201d in this panel.",
        Component: FacetList,
        tileProps: { cols: 2, rows: 2, col: 7, row: 2, category: "patient-characteristics" },
        facetProps: { category: "patient-characteristics" },
    },
    {
        key: "sex",
        title: "Sex",
        iconHover: "Patient-level data. Each publication reports data on one or more patients, and each patient may have outcomes associated with one or more treatments.",
        description: "Indicates the patient\u2019s sex as reported in the case.",
        Component: FacetPie,
        tileProps: { type: "pie", cols: 2, rows: 1, col: 5, row: 2, category: "patient-characteristics" },
        facetProps: { category: "patient-characteristics" },
    },
    {
        key: "ageBuckets",
        title: "Age",
        iconHover: "Patient-level data. Each publication reports data on one or more patients, and each patient may have outcomes associated with one or more treatments.",
        description: "Indicates the patient\u2019s age as reported in the case and grouped into predefined ranges.",
        Component: FacetBar,
        tileProps: { cols: 2, rows: 1, col: 5, row: 3, category: "patient-characteristics" },
        facetProps: { category: "patient-characteristics" },
    },

    // --- biomarker-characteristics ---
    {
        key: "biomarkerTypes",
        title: "Biomarker class",
        iconHover: "Treatment-level data. Each publication reports data on one or more patients, and each patient may have outcomes associated with one or more treatments.",
        description: "Indicates the class of biomarker reported to guide treatment selection. If multiple biomarkers involving different classes are used for a given treatment, each is displayed separately in this panel.",
        Component: FacetPie,
        tileProps: { type: "pie", cols: 2, rows: 1, col: 1, row: 5, category: "treatment-characteristics" },
        facetProps: { category: "treatment-characteristics" },
    },
    {
        key: "clinicalResponse",
        title: "Clinical Response",
        iconHover: "Treatment-level data. Each publication reports data on one or more patients, and each patient may have outcomes associated with one or more treatments.",
        description: "Indicates the best overall response reported for the treatment according to RECIST criteria.",
        Component: FacetPie,
        tileProps: { type: "pie", cols: 2, rows: 1, col: 1, row: 6, category: "treatment-characteristics" },
        facetProps: { category: "treatment-characteristics" },
    },
    {
        key: "biomarkerGenes",
        title: "Biomarker",
        iconHover: "Treatment-level data. Each publication reports data on one or more patients, and each patient may have outcomes associated with one or more treatments.",
        description: "Indicates the biomarker reported to guide treatment selection. If multiple biomarkers are used for a given treatment, each is displayed separately in this panel.",
        Component: FacetList,
        tileProps: { type: "list", cols: 2, rows: 2, col: 3, row: 5, category: "treatment-characteristics" },
        facetProps: { category: "treatment-characteristics" },
    },
    {
        key: "biomarkerMeasurements",
        title: "Biomarker details",
        iconHover: "Treatment-level data. Each publication reports data on one or more patients, and each patient may have outcomes associated with one or more treatments.",
        description: "Indicates details of the biomarker reported to guide treatment selection. If multiple biomarkers are used for a given treatment, these are detailed together and separated by \u201cAND\u201d in this panel.",
        Component: FacetList,
        tileProps: { type: "list", cols: 2, rows: 2, col: 5, row: 5, category: "treatment-characteristics" },
        facetProps: { category: "treatment-characteristics" },
    },

    //-- manuscript-characteristics ---
    {
        key: "countries",
        title: "Publication country",
        iconHover: "Manuscript-level data. Each publication reports data on one or more patients, and each patient may have outcomes associated with one or more treatments.",
        description: "Indicates the country of the last author of the publication.",
        Component: FacetList,
        tileProps: { cols: 2, rows: 2, col: 7, row: 5, category: "manuscript-characteristics" },
        facetProps: { category: "manuscript-characteristics" },
    },
    {
        key: "years",
        title: "Publication year",
        iconHover: "Manuscript-level data. Each publication reports data on one or more patients, and each patient may have outcomes associated with one or more treatments.",
        description: "Indicates the year of the publication.",
        Component: FacetBar,
        tileProps: { cols: 2, rows: 1, col: 7, row: 7, category: "manuscript-characteristics" },
        facetProps: { category: "manuscript-characteristics" },
    },

    // --- treatment-characteristics ---
    {
        key: "drugTypes",
        title: "Treatment class",
        iconHover: "Treatment-level data. Each publication reports data on one or more patients, and each patient may have outcomes associated with one or more treatments.",
        description: "Indicates the class of the drug for pharmaceutical treatments. If multiple drug classes are used in combination, each is displayed separately in this panel.",
        Component: FacetList,
        tileProps: { cols: 2, rows: 2, col: 3, row: 7, category: "treatment-characteristics" },
        facetProps: { category: "treatment-characteristics" },
    },
    {
        key: "drugNames",
        title: "Treatment details",
        iconHover: "Treatment-level data. Each publication reports data on one or more patients, and each patient may have outcomes associated with one or more treatments.",
        description: "Indicates the reported drug for pharmaceutical treatments. If multiple drugs are used in combination, these are detailed together and separated by \u201cAND\u201d in this panel.",
        Component: FacetList,
        tileProps: { type: "list", cols: 2, rows: 2, col: 5, row: 7, category: "treatment-characteristics" },
        facetProps: { category: "treatment-characteristics" },
    },
    {
        key: "kaplanMeier",
        title: "Time on treatment",
        iconHover: "Treatment-level data. Each publication reports data on one or more patients, and each patient may have outcomes associated with one or more treatments.",
        description: "Indicates the duration of the treatment from initiation to discontinuation or last follow-up.",
        Component: FacetKaplanMeier,
        tileProps: { cols: 2, rows: 1, col: 1, row: 8, category: "treatment-characteristics" },
        facetProps: { category: "treatment-characteristics"},
    },
    {
        key: "reasonForDiscontinuation",
        title: "Reason for treatment discontinuation",
        iconHover: "Treatment-level data. Each publication reports data on one or more patients, and each patient may have outcomes associated with one or more treatments.",
        description: "Indicates the reason reported for discontinuing the treatment.",
        Component: FacetPie,
        tileProps: { type: "pie", cols: 2, rows: 1, col: 1, row: 7, category: "treatment-characteristics" },
        facetProps: { category: "treatment-characteristics" },
    },
    {
        key: "adverseEvents",
        title: "Adverse Events",
        iconHover: "Treatment-level data. Each publication reports data on one or more patients, and each patient may have outcomes associated with one or more treatments.",
        description: "Indicates adverse events reported for a given treatment, mapped to CTCAE categories. If multiple adverse events involving different categories are reported, each is displayed separately in this panel.",
        Component: FacetList,
        tileProps: { type: "list", cols: 2, rows: 2, col: 3, row: 9, category: "treatment-characteristics" },
        facetProps: { category: "treatment-characteristics" },
    },
    {
        key: "aeGrade",
        title: "Adverse Event Grade",
        iconHover: "Treatment-level data. Each publication reports data on one or more patients, and each patient may have outcomes associated with one or more treatments.",
        description: "Indicates the severity of adverse events according to CTCAE grading.",
        Component: FacetPie,
        tileProps: { type: "pie", cols: 2, rows: 1, col: 1, row: 9, category: "treatment-characteristics" },
        facetProps: { category: "treatment-characteristics" },
    },
];